import { invariant } from "@banjoanton/utils";
import type { Square } from "chess.js";
import { ChessPiece } from "../../../../models/base-model";
import { BoardQuery } from "../../../../utils/board-query";
import { ExchangeUtil } from "../../../../utils/exchange-util";
import { type ForkTarget, MoveAnnotation } from "../../models/move-annotation";
import type { CalculateAnnotationFunction } from "../annotation-service";

type RateForkProps = {
    attacker: ChessPiece;
    targets: ForkTarget[];
    canBeCaptured: boolean;
    netGainOnCapture: number;
};

const rateFork = ({
    attacker,
    targets,
    canBeCaptured,
    netGainOnCapture,
}: RateForkProps): number => {
    // If the net gain is negative, it's not a good fork
    if (netGainOnCapture < 0) {
        return netGainOnCapture;
    }

    const willWinAgainstSomeTarget = targets.some((t) => {
        const netGainForTarget = ExchangeUtil.calculateExchange({
            target: t.piece,
            attackers: t.attackers,
            defenders: t.defenders,
        });
        return netGainForTarget > 0;
    });

    if (!willWinAgainstSomeTarget) {
        return -1; // not a good fork if all targets are defended
    }

    const attackerValue = ChessPiece.value(attacker);
    let score = 0;

    for (const target of targets) {
        const value = ChessPiece.value(target.piece);
        if (target.piece === "k") {
            score += 3; // attacking king is valuable, but not considered Infinity as the piece is valued
        } else if (target.defenders.length === 0) {
            score += value; // hanging
        } else {
            const netGainForTarget = ExchangeUtil.calculateExchange({
                target: target.piece,
                attackers: target.attackers,
                defenders: target.defenders,
            });

            if (netGainForTarget > 0) {
                score += netGainForTarget;
            }
        }
    }

    score += netGainOnCapture;

    // Penalize if attacker can be captured after the fork
    if (canBeCaptured) {
        score -= attackerValue;
    }

    return score;
};

const calculate: CalculateAnnotationFunction = async ({ move }) => {
    const positionAfterMove = BoardQuery.fromMove(move);
    const opponentPieces = positionAfterMove.pieces(positionAfterMove.turn);

    const squareAttackMap: Partial<Record<Square, ForkTarget[]>> = {};

    for (const opponentPiece of opponentPieces) {
        const attackers = positionAfterMove.attackers(opponentPiece.square);
        const defenders = positionAfterMove.defenders(opponentPiece.square);

        for (const attacker of attackers) {
            const existingTargets = squareAttackMap[attacker.square] ?? [];
            existingTargets.push({
                square: opponentPiece.square,
                piece: opponentPiece.type,
                defenders: defenders.map((d) => d.type),
                attackers: attackers.map((a) => a.type),
            });
            squareAttackMap[attacker.square] = existingTargets;
        }
    }

    const forks = Object.entries(squareAttackMap).filter(
        ([_, targets]) => targets && targets.length >= 2
    );

    if (forks.length === 0) {
        return [];
    }

    const annotations = forks.map(([attacker, targets]) => {
        const square = attacker as Square;
        const attackingPiece = positionAfterMove.pieceAt(square);
        invariant(
            attackingPiece,
            `Expected attacking piece at square ${attacker}`
        );

        const squareAttackers = positionAfterMove.attackers(square);
        const squareDefenders = positionAfterMove.defenders(square);

        const canBeCaptured = squareAttackers.length > 0;

        const capture = ChessPiece.parse(move.captured);

        // Evaluate the static exchange on the capture square
        // basically the best net gain after potential recaptures for the forker
        const netGain = ExchangeUtil.calculateExchange({
            target: capture,
            attackers: squareAttackers.map((a) => a.type),
            defenders: squareDefenders.map((d) => d.type),
        });

        const rating = rateFork({
            attacker: attackingPiece.type,
            targets,
            canBeCaptured,
            netGainOnCapture: netGain,
        });

        const isCheck = targets.some((s) => s.piece === "k");

        return MoveAnnotation.from({
            type: "fork",
            square: attacker,
            attacker: attackingPiece.type,
            canBeCaptured,
            targets,
            isCheck,
            rating,
            capture,
            squareAttackers: squareAttackers.map((a) => a.type),
            squareDefenders: squareDefenders.map((d) => d.type),
            netGain,
        });
    });

    return annotations;
};

export const ForkAnnotationService = { calculate };
